import { useState, useEffect, useCallback } from "react";
import { Bell, Check, CheckCheck, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import apiClient from "../services/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import { syncAppBadge } from "../lib/appBadge";
const BASE_TITLE = "V2R Masonry";

// Play notification sound using Web Audio API
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create a pleasant two-tone chime
    const playTone = (frequency, startTime, duration) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = "sine";
      
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.05);
      gainNode.gain.linearRampToValueAtTime(0, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    const now = audioContext.currentTime;
    playTone(880, now, 0.15);      // A5
    playTone(1100, now + 0.15, 0.2); // C#6
    
  } catch (error) {
    console.log("Could not play notification sound:", error);
  }
};

const NotificationBell = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [prevUnreadCount, setPrevUnreadCount] = useState(0);

  // Update browser tab title with unread count
  useEffect(() => {
    if (unreadCount > 0) {
      document.title = `(${unreadCount}) ${BASE_TITLE}`;
    } else {
      document.title = BASE_TITLE;
    }
    
    // Cleanup on unmount
    return () => {
      document.title = BASE_TITLE;
    };
  }, [unreadCount]);

  // Mirror the app-icon badge (red bubble) to the unread count. Merely opening
  // the app never marks anything read, so the badge only clears as the user
  // reads notifications in the panel.
  useEffect(() => {
    syncAppBadge(unreadCount);
  }, [unreadCount]);

  // Play sound when new notifications arrive
  useEffect(() => {
    if (unreadCount > prevUnreadCount && prevUnreadCount !== 0) {
      playNotificationSound();
    }
    setPrevUnreadCount(unreadCount);
  }, [unreadCount, prevUnreadCount]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data } = await apiClient.get(`/notifications/app-notifications`);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initial fetch and polling
  useEffect(() => {
    fetchNotifications();
    
    // Poll every 2 minutes for new notifications
    const interval = setInterval(fetchNotifications, 2 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Refetch when panel opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await apiClient.post(`/notifications/app-notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiClient.post(`/notifications/app-notifications/mark-all-read`);
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const dismissNotification = async (notificationId, e) => {
    e.stopPropagation(); // Prevent triggering the parent click

    try {
      await apiClient.delete(`/notifications/app-notifications/${notificationId}`);
      // Remove from local state
      const notification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));

      // Update unread count if the dismissed notification was unread
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Failed to dismiss notification:", error);
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "";
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type) => {
    // Return different colors based on notification type
    switch (type) {
      case "bid_due":
        return "bg-yellow-500";
      case "billing_due":
        return "bg-blue-500";
      case "invoice_due":
        return "bg-green-500";
      case "missing_report":
        return "bg-red-500";
      case "backup_reminder":
        return "bg-purple-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-12 w-12 sm:h-[60px] sm:w-[60px] rounded-full"
          data-testid="notification-bell-btn"
        >
          <Bell className="w-[30px] h-[30px]" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute top-1 right-1 h-6 min-w-[24px] px-1.5 py-0 text-sm flex items-center justify-center"
              data-testid="notification-unread-count"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="end"
        data-testid="notification-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h4 className="font-semibold text-sm">Notifications</h4>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs text-muted-foreground hover:text-foreground"
              onClick={markAllAsRead}
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-4 h-4 mr-1" />
              Mark all read
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <ScrollArea className="h-[300px]">
          {loading && notifications.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              Loading...
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm py-8">
              <Bell className="w-8 h-8 mb-2 opacity-50" />
              <p>No notifications</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`group px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer ${
                    !notification.is_read ? "bg-muted/30" : ""
                  }`}
                  onClick={() => {
                    if (!notification.is_read) {
                      markAsRead(notification.id);
                    }
                  }}
                  data-testid={`notification-item-${notification.id}`}
                >
                  <div className="flex gap-3">
                    {/* Type indicator dot */}
                    <div className="flex-shrink-0 mt-1.5">
                      <div
                        className={`w-2 h-2 rounded-full ${getNotificationIcon(notification.type)}`}
                      />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {notification.title && (
                        <p className="text-sm font-medium text-foreground truncate">
                          {notification.title}
                        </p>
                      )}
                      <p className={`text-sm ${notification.title ? "text-muted-foreground" : "text-foreground"}`}>
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatTimeAgo(notification.created_at)}
                      </p>
                    </div>

                    {/* Actions: Read indicator or Dismiss button */}
                    <div className="flex-shrink-0 flex items-start gap-1">
                      {!notification.is_read && (
                        <div className="w-2 h-2 rounded-full bg-primary mt-1.5" />
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                        onClick={(e) => dismissNotification(notification.id, e)}
                        title="Dismiss"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {notifications.length > 0 && (
          <>
            <Separator />
            <div className="p-2">
              <p className="text-xs text-center text-muted-foreground">
                Read notifications auto-hide after 24h
              </p>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
